import React, { useState } from 'react';
import axios from 'axios';

import { Button, Field, Input } from '../../components/ui';
import { requireResponse } from '../../core/errors/requireResponse';
import { ROLES } from './teamRoles';

/**
 * Add a person to this store by mobile number (F2).
 *
 * POST /api/stores/{id}/people creates the account and seats them in one step,
 * already approved, so they can sign in straight away with a code sent by SMS.
 * Someone who already has an account is added with the picker below instead
 * (the backend answers 409 user_exists rather than make a second account).
 */
export function AddPersonForm({ storeId, onAdded }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('sales_admin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [added, setAdded] = useState('');

  const canSubmit = !busy && firstName.trim() !== '' && phone.trim() !== '';

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;

    setBusy(true);
    setError('');
    setAdded('');

    try {
      const res = requireResponse(
        await axios.post(`/api/stores/${storeId}/people`, {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim(),
          role,
        })
      );
      const name = firstName.trim();

      setAdded(`${name} can now sign in with ${res.data?.phone || phone.trim()}.`);
      setFirstName('');
      setLastName('');
      setPhone('');
      setRole('sales_admin');
      onAdded?.();
    } catch (err) {
      const data = err?.response?.data;
      const message = data?.message || 'We couldn’t add that person.';

      setError(
        data?.code === 'user_exists'
          ? `${message} Use “Add someone who already has an account” below.`
          : message
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="rc-invite rc-add-person" onSubmit={submit} noValidate>
      <h4 className="rc-invite__title">Add your team</h4>
      <p className="rc-invite__intro">
        Add each person with their mobile number. They sign in with a code sent by SMS.
      </p>

      <div className="rc-invite__form">
        <Field label="First name" required>
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Last name">
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Mobile" required hint="They sign in with a code sent to this number">
          <Input type="tel" value={phone} placeholder="+44 7700 900200" onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="Role" hint={ROLES.find((r) => r.value === role)?.hint}>
          <select className="ma-role-select" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
      </div>

      <Button type="submit" variant="primary" size="lg" disabled={!canSubmit} loading={busy}>
        Add person
      </Button>

      {error ? <p className="rc-invite__error" role="alert">{error}</p> : null}
      {added ? <p className="rc-invite__sentline" role="status">{added}</p> : null}
    </form>
  );
}

export default AddPersonForm;
